// src/hooks/useTrash.js
import { useSelector, useDispatch } from "react-redux";
import { useCallback, useMemo, useEffect } from "react";
import { message } from "antd";
import { useTranslation } from "react-i18next";

import {
  fetchDeletedDocuments,
  restoreDocument,
  permanentlyDeleteDocument,
  bulkRestoreDocuments,
  bulkDeleteDocuments,
  emptyTrash,
} from "../redux/thunks/trashThunks";

import {
  setSelectedRowKeys,
  clearSelection,
  setSearchText,
  setFilterLocation,
  toggleSortOrder,
} from "../redux/slices/trashSlice";

export default function useTrash() {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const {
    items,
    total,
    totalSize,
    loading,
    error,
    selectedRowKeys,
    searchText,
    sortOrder,
    filterLocation,
    isRestoring,
    isDeleting,
    isEmptying,
    isMovingToTrash,
  } = useSelector((state) => state.trash);

  // ✅ initial fetch
  useEffect(() => {
    dispatch(fetchDeletedDocuments({ skip: 0, limit: 50 }));
  }, [dispatch]);

  // ✅ Debug des types reçus
  useEffect(() => {
    const counts = {};
    (items || []).forEach((it) => {
      const k = it.canonicalType || it.type || "-";
      counts[k] = (counts[k] || 0) + 1;
    });
  }, [items]);

  const filteredData = useMemo(() => {
    let result = [...(items || [])];

    // Search
    if (searchText) {
      const query = searchText.toLowerCase();
      result = result.filter(
        (item) =>
          (item.fileName || "").toLowerCase().includes(query) ||
          (item.location || "").toLowerCase().includes(query) ||
          (item.type || "").toLowerCase().includes(query)
      );
    }

    /**
     * ✅ IMPORTANT
     * Ton Select (Biochemistry Tests / Others) doit filtrer par TYPE
     * pas par location (qui est "-")
     */
    if (filterLocation && filterLocation !== "all") {
      result = result.filter(
        (item) => (item.canonicalType || "") === filterLocation
      );
    }

    const toTime = (d) => {
      if (!d || d === "-") return 0;
      const dt = new Date(d);
      return Number.isNaN(dt.getTime()) ? 0 : dt.getTime();
    };

    result.sort((a, b) => {
      const dateA = toTime(a.deletedDate);
      const dateB = toTime(b.deletedDate);
      return sortOrder === "asc" ? dateA - dateB : dateB - dateA;
    });

    return result;
  }, [items, searchText, filterLocation, sortOrder]);

  const selectedCount = selectedRowKeys.length;
  const isTrashEmpty = (items || []).length === 0;
  const hasSelection = selectedCount > 0;

  const isAnyOperationPending =
    loading || isRestoring || isDeleting || isEmptying || isMovingToTrash;

  const getTrashId = (record) =>
    record?.trashId || record?._id || record?.id || record?.key;

  const handleRestoreDocument = useCallback(
    async (record) => {
      const trashId = getTrashId(record);
      if (!trashId) {
        message.error("Missing trashId (cannot restore).");
        return;
      }

      try {
        await dispatch(restoreDocument({ trashId })).unwrap();
        message.success(
          t("wastebasket.messages.restoreSuccess", "Document restored successfully")
        );
      } catch (err) {
        message.error(
          err || t("wastebasket.messages.restoreError", "Failed to restore document")
        );
      }
    },
    [dispatch, t]
  );

  const handlePermanentlyDeleteDocument = useCallback(
    async (record) => {
      const trashId = getTrashId(record);
      if (!trashId) {
        message.error("Missing trashId (cannot delete).");
        return;
      }

      try {
        await dispatch(permanentlyDeleteDocument({ trashId })).unwrap();
        message.success(
          t("wastebasket.messages.deleteSuccess", "Document permanently deleted")
        );
      } catch (err) {
        message.error(
          err || t("wastebasket.messages.deleteError", "Failed to delete document")
        );
      }
    },
    [dispatch, t]
  );

  const handleBulkRestore = useCallback(async () => {
    if (selectedRowKeys.length === 0) return;

    try {
      await dispatch(
        bulkRestoreDocuments({ documentIds: selectedRowKeys })
      ).unwrap();

      message.success(
        t("wastebasket.messages.bulkRestoreSuccess", {
          count: selectedRowKeys.length,
          defaultValue: `${selectedRowKeys.length} documents restored`,
        })
      );
    } catch (err) {
      message.error(
        err || t("wastebasket.messages.bulkRestoreError", "Failed to restore documents")
      );
    }
  }, [dispatch, selectedRowKeys, t]);

  const handleBulkDelete = useCallback(async () => {
    if (selectedRowKeys.length === 0) return;

    try {
      await dispatch(
        bulkDeleteDocuments({ documentIds: selectedRowKeys })
      ).unwrap();

      message.success(
        t("wastebasket.messages.bulkDeleteSuccess", {
          count: selectedRowKeys.length,
          defaultValue: `${selectedRowKeys.length} documents deleted`,
        })
      );
    } catch (err) {
      message.error(
        err || t("wastebasket.messages.bulkDeleteError", "Failed to delete documents")
      );
    }
  }, [dispatch, selectedRowKeys, t]);

  const handleEmptyTrash = useCallback(async () => {
    try {
      await dispatch(emptyTrash()).unwrap();
      message.success(
        t("wastebasket.messages.emptySuccess", "Trash emptied successfully")
      );
    } catch (err) {
      message.error(err || t("wastebasket.messages.emptyError", "Failed to empty trash"));
    }
  }, [dispatch, t]);

  const handleSelectionChange = useCallback(
    (newSelectedRowKeys) => {
      dispatch(setSelectedRowKeys(newSelectedRowKeys));
    },
    [dispatch]
  );

  const handleClearSelection = useCallback(() => {
    dispatch(clearSelection());
  }, [dispatch]);

  const handleSearchChange = useCallback(
    (value) => {
      dispatch(setSearchText(value));
    },
    [dispatch]
  );

  const handleSortOrderChange = useCallback(() => {
    dispatch(toggleSortOrder());
  }, [dispatch]);

  const handleFilterLocationChange = useCallback(
    (value) => {
      dispatch(setFilterLocation(value));
    },
    [dispatch]
  );

  const refreshTrash = useCallback(() => {
    dispatch(fetchDeletedDocuments({ skip: 0, limit: 50 }));
  }, [dispatch]);

  return {
    items,
    total,
    totalSize,
    filteredData,
    loading,
    error,

    selectedRowKeys,
    selectedCount,

    searchText,
    sortOrder,
    filterLocation,

    isRestoring,
    isDeleting,
    isEmptying,
    isMovingToTrash,

    isTrashEmpty,
    hasSelection,
    isAnyOperationPending,

    handleRestoreDocument,
    handlePermanentlyDeleteDocument,
    handleBulkRestore,
    handleBulkDelete,
    handleEmptyTrash,

    handleSelectionChange,
    handleClearSelection,
    handleSearchChange,
    handleSortOrderChange,
    handleFilterLocationChange,

    refreshTrash,
  };
}
